import {
  Button,
  Divider,
  Group,
  Modal,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconBug, IconCheck, IconReceipt } from "@tabler/icons-react";
import { Frequency, type PlanConfig } from "core";
import { useSearchParams } from "react-router-dom";
import { formatCents } from "../../utils/amount";
import { formatDate } from "../../utils/date";
import { SearchParam } from "../../utils/searchParams";
import { trpc } from "../../utils/trpc";
import { useAccountMutation } from "../../utils/useAccountMutation";
import { InvoiceBreakdown } from "./InvoiceBreakdown";
import classes from "./PricingModal.module.css";

function planChangeTitle(currentPlanName: string | null, plan: PlanConfig) {
  if (!currentPlanName) return plan.name;
  if (currentPlanName === plan.name) {
    const switching =
      plan.interval === Frequency.Year
        ? "monthly to yearly"
        : "yearly to monthly";
    return `${plan.name}, ${switching}`;
  }
  return `From ${currentPlanName} to ${plan.name}`;
}

export function ChangePlanModal({
  plan,
  currentPlanName,
  confirmLabel,
  opened,
  onClose,
}: {
  plan?: PlanConfig;
  currentPlanName: string | null;
  confirmLabel?: string;
  opened: boolean;
  onClose: () => void;
}) {
  const [, setSearchParams] = useSearchParams();
  const { changePlan, isChangingPlan } = useAccountMutation();
  const {
    data: preview,
    isPending: isLoadingPreview,
    isError: isPreviewError,
  } = trpc.account.previewPlanChange.useQuery(
    {
      planSlug: plan?.planSlug ?? "",
      interval: plan?.interval ?? Frequency.Month,
    },
    { enabled: opened && !!plan },
  );

  const isUpgrade = preview?.direction === "upgrade";
  const switchDate = preview?.effectiveAt
    ? formatDate(preview.effectiveAt)
    : null;

  function onConfirm() {
    if (!plan || !preview) return;

    changePlan(
      { planSlug: plan.planSlug, interval: plan.interval },
      {
        onSuccess() {
          notifications.show({
            message: switchDate
              ? `Your plan switches to ${plan.name} on ${switchDate}.`
              : `You're now on ${plan.name}.`,
            color: "green",
            icon: <IconCheck size={18} />,
          });
          onClose();
          setSearchParams(
            (params) => {
              params.delete(SearchParam.Upgrade);
              return params;
            },
            { replace: true },
          );
        },
        onError() {
          notifications.show({
            message:
              "The plan change didn't go through. Check your payment method or contact support.",
            color: "red",
            icon: <IconBug size={18} />,
          });
        },
      },
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="md"
      radius="lg"
      title="Change plan"
    >
      <Stack gap="lg">
        <Group justify="center" pt="sm">
          <ThemeIcon
            size={72}
            radius="xl"
            variant="light"
            color="orange"
            className={classes.receiptIcon}
          >
            <IconReceipt size={32} />
          </ThemeIcon>
        </Group>

        <Stack gap="sm">
          <Group justify="space-between" align="baseline">
            <Text c="dimmed">Plan</Text>
            <Text>{plan && planChangeTitle(currentPlanName, plan)}</Text>
          </Group>

          {isLoadingPreview && !isPreviewError && (
            <Skeleton height={16} radius="sm" />
          )}

          {isPreviewError && (
            <Text size="sm" c="red">
              We couldn't calculate this change. Try again or contact support.
            </Text>
          )}

          {preview && isUpgrade && (
            <Group justify="space-between" align="baseline">
              <Text size="sm" c="dimmed">
                {preview.creditToday
                  ? "Credited to your next invoice"
                  : "Charged today"}
              </Text>
              <Text size="sm">
                {formatCents(
                  preview.creditToday || preview.dueToday,
                  preview.currency,
                )}
              </Text>
            </Group>
          )}

          {preview && !isUpgrade && (
            <Text size="sm" c="dimmed">
              {switchDate
                ? `Your plan switches on ${switchDate}. Nothing is charged today.`
                : "Switches now, nothing is charged during your trial."}
            </Text>
          )}

          {preview && <InvoiceBreakdown {...preview} />}

          <Divider />

          <Group justify="space-between" align="baseline">
            <Text fw={800}>{switchDate ? "From then" : "New total"}</Text>
            {preview ? (
              <Group gap={6} align="baseline">
                <Text className={classes.priceAmount}>
                  {formatCents(preview.recurringTotal, preview.currency)}
                </Text>
                <Text size="sm" className={classes.priceFrequency}>
                  /{plan?.interval}
                </Text>
              </Group>
            ) : (
              !isPreviewError && <Skeleton height={32} width={120} />
            )}
          </Group>
        </Stack>

        <Group gap="sm" justify="flex-end">
          <Button onClick={onClose} variant="default">
            Cancel
          </Button>
          <Button
            loading={isChangingPlan}
            disabled={!preview}
            onClick={onConfirm}
          >
            {confirmLabel ?? (isUpgrade ? "Upgrade" : "Downgrade")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
